import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { noop } from "lodash";
import videojs from "video.js";
import { t } from "@/i18n-js/instance";
import { isSafari } from "@circle-react/helpers/browserHelpers";
import { Button } from "@circle-react-uikit/Button";
import { PlayerStats } from "./PlayerStats";
import "./styles.scss";
import "video.js/dist/video-js.css";

export const VideoJSPlayer = ({
  hlsUrl,
  onDoubleClick = noop,
  showControls = false,
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(isSafari());
  const [showStats, setShowStats] = useState(false);

  const playWithAudio = () => {
    setMuted(false);
    playerRef.current.play();
    playerRef.current.muted(false);
  };

  const handleOnContextMenu = e => {
    e.preventDefault();
    setShowStats(!showStats);
  };

  useEffect(() => {
    if (hlsUrl) {
      const options = {
        autoplay: true,
        playsinline: true,
        controls: showControls,
        muted: isSafari(),
        html5: {
          vhs: {
            overrideNative: true,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
        sources: [
          {
            src: hlsUrl,
            type: "application/x-mpegURL",
          },
        ],
      };

      // Make sure Video.js player is only initialized once
      if (!playerRef.current) {
        // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
        const videoElement = document.createElement("video-js");

        videoElement.classList.add("vjs-big-play-centered");
        videoRef.current.appendChild(videoElement);

        playerRef.current = videojs(videoElement, options, () => {
          videojs.log("player is ready");
        });

        // You could update an existing player in the `else` block here
        // on prop change, for example:
      } else {
        const player = playerRef.current;

        player.autoplay(options.autoplay);
        player.src(options.sources);
        player.controls(options.controls);
      }
    }
  }, [hlsUrl, videoRef, showControls]);

  // Dispose the Video.js player when the functional component unmounts
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  return (
    <>
      <div
        className="relative flex h-full w-full flex-col justify-center"
        onDoubleClick={onDoubleClick}
        data-vjs-player
      >
        <div
          className="hls-player"
          ref={videoRef}
          onContextMenu={handleOnContextMenu}
        />
        {playerRef.current && showStats && (
          <PlayerStats player={playerRef.current} />
        )}
      </div>
      {muted && (
        <div className="bg-modal-overlay fixed left-0 top-0 flex h-screen w-full items-center justify-center">
          <Button onClick={playWithAudio}>
            {t("live_streams.play_with_audio")}
          </Button>
        </div>
      )}
    </>
  );
};

VideoJSPlayer.propTypes = {
  hlsUrl: PropTypes.string,
  onDoubleClick: PropTypes.func,
  showControls: PropTypes.bool,
};
